/**
 * Converts an image to 1:1 aspect ratio (450x450) with white background
 * @param {File} file - The image file to convert
 * @returns {Promise<File>} - The converted image file
 */
export const convertImageToSquare = async (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target.result;
    };

    img.onload = () => {
      // Create canvas with 450x450 dimensions
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const targetSize = 450;
      
      canvas.width = targetSize;
      canvas.height = targetSize;

      // Fill with white background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, targetSize, targetSize);

      // Calculate scaling to fit image while maintaining aspect ratio
      const scale = Math.min(targetSize / img.width, targetSize / img.height);
      const scaledWidth = img.width * scale;
      const scaledHeight = img.height * scale;

      // Center the image
      const x = (targetSize - scaledWidth) / 2;
      const y = (targetSize - scaledHeight) / 2;

      // Draw the image on canvas
      ctx.drawImage(img, x, y, scaledWidth, scaledHeight);

      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to convert image'));
          return;
        }

        // Create new File from blob with original filename
        const convertedFile = new File([blob], file.name, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });

        resolve(convertedFile);
      }, 'image/jpeg', 0.95);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Check if an image is square (1:1 aspect ratio)
 * @param {File} file - The image file to check
 * @returns {Promise<boolean>} - True if image is square, false otherwise
 */
export const isImageSquare = async (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target.result;
    };

    img.onload = () => {
      resolve(img.width === img.height);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Check if a GHL media file URL is square by loading the image
 * @param {string} url - The image URL to check
 * @returns {Promise<boolean>} - True if image is square, false otherwise
 */
export const isImageUrlSquare = async (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      resolve(img.width === img.height);
    };

    img.onerror = () => {
      // If we can't load the image, assume it's not square
      resolve(true); // Return true to avoid showing button on error
    };

    img.src = url;
  });
};

/**
 * Convert an image URL to a square image and return as File
 * @param {string} url - The image URL to convert
 * @param {string} filename - The filename for the converted file
 * @returns {Promise<File>} - The converted image file
 */
export const convertImageUrlToSquare = async (url, filename) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      // Create canvas with 450x450 dimensions
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const targetSize = 450;
      
      canvas.width = targetSize;
      canvas.height = targetSize;

      // Fill with white background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, targetSize, targetSize);

      // Calculate scaling to fit image while maintaining aspect ratio
      const scale = Math.min(targetSize / img.width, targetSize / img.height);
      const scaledWidth = img.width * scale;
      const scaledHeight = img.height * scale;

      // Center the image
      const x = (targetSize - scaledWidth) / 2;
      const y = (targetSize - scaledHeight) / 2;

      // Draw the image on canvas
      ctx.drawImage(img, x, y, scaledWidth, scaledHeight);

      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to convert image'));
          return;
        }

        // Create File from blob
        const convertedFile = new File([blob], filename, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });

        resolve(convertedFile);
      }, 'image/jpeg', 0.95);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image from URL'));
    };

    img.src = url;
  });
};

/**
 * Batch convert multiple files to square
 * @param {File[]} files - Array of files to convert
 * @returns {Promise<File[]>} - Array of converted files
 */
export const batchConvertToSquare = async (files) => {
  const convertPromises = files.map(file => convertImageToSquare(file));
  return Promise.all(convertPromises);
};
